import { useEffect, useRef } from "react";
import Shepherd from "shepherd.js";
import "shepherd.js/dist/css/shepherd.css";
import { TEAM_INVITES_UI_ENABLED, CONTENT_ENABLED } from "@/lib/featureFlags";

const TOUR_KEY = "sellix_tour_seen";
const AUTH_KEY = "sellix.auth.v1";

const readAuth = () => { try { return JSON.parse(localStorage.getItem(AUTH_KEY) || "null"); } catch { return null; } };

// Persona-aware copy overrides (step id → { title, text })
const personaCopy = {
  "sales-leader": {
    welcome: { title: "Pipeline-first view of Sellyx", text: "Sales Leader cut — Maya runs as you, your numbers tell the story. 90 seconds." },
    today: { title: "Pipeline this morning, in plain English", text: "Maya's overnight summary translates ops into pipeline language. Click any number to drill in." },
    performance: { title: "The ROI slide for your CFO", text: "$842K attributed, 5.2 FTE replaced, 1,184h saved. Export-ready." },
  },
  "founder": {
    welcome: { title: "Sellyx — your personal AI SDR", text: "Maya runs as you across WhatsApp, LinkedIn, Gmail, Telegram. Replaces a $120k SDR for $999/mo." },
    today: { title: "Your morning, in 60 seconds", text: "Maya replied to 7 WhatsApps, booked 2 demos, drafted 3 follow-ups — while you slept." },
    performance: { title: "What Maya saves you", text: "1,184h of SDR time. $842K attributed pipeline. 5.2 FTE replaced. Real numbers, your data." },
  },
  "revops": {
    welcome: { title: "RevOps cut of Sellyx", text: "Signals, sequences, attribution — but operated by a single AI as one team member, not a SaaS bolt-on." },
    sequence: { title: "Visual sequence builder", text: "Drag steps, A/B test, Maya drafts copy in your voice. Same primitives across channels." },
    performance: { title: "Attribution + agent economics", text: "Multi-touch attribution rolls into per-account ROI. CAC math finally makes sense." },
  },
  "sdr": {
    welcome: { title: "Maya is your co-pilot, not your replacement", text: "She handles tier-1, drafts replies in your voice, books meetings. You ship the closes." },
    inbox: { title: "Every channel, one inbox", text: "WhatsApp + LinkedIn + Gmail + Telegram in one cockpit. Maya sorts by urgency." },
    suggest: { title: "Maya drafts the reply", text: "Ask Maya. She streams a reply into your composer in your voice. Edit and ship — or accept." },
  },
};

const baseSteps = [
  { id: "welcome",     route: "/today", attach: '[data-testid="sellix-logo"]',        placement: "right",  title: "Welcome to Sellyx",                  text: "Your personal AI SDR — Maya, running as you on WhatsApp, LinkedIn, Gmail and Telegram. 90 seconds." },
  { id: "today",       route: "/today", attach: '[data-testid="overnight-summary"]',  placement: "bottom", title: "Today briefing",                     text: "Maya's overnight summary, in her voice. Numbers are clickable — they jump to the right screen." },
  { id: "decisions",   route: "/today", attach: '[data-testid="needs-decision"]',     placement: "top",    title: "Needs your decision",                text: "Six items Maya needs your sign-off on. Approve, edit, or take over — sorted by who owns each thread." },
  { id: "inbox",       route: "/inbox", attach: '[data-testid="conversation-list"]',  placement: "right",  title: "Unified across paired accounts",     text: "Every paired account — Aman's WhatsApp, LinkedIn, Gmail, Telegram — in one inbox. Maya sorts by urgency." },
  { id: "trace",       route: "/inbox", attach: '[data-testid="tab-trace"]',          placement: "left",   title: "Maya's reasoning",                    text: "Every tool call Maya made — searchContact, getDocument, searchDeal — with full params and results. Auditable, never magic." },
  { id: "suggest",     route: "/inbox", attach: '[data-testid="ai-suggest-btn"]',     placement: "top",    title: "Ask Maya to draft a reply",          text: "Click and watch — Maya streams a reply character-by-character right into your composer, in your voice. Ship when ready." },
  { id: "people",      route: "/people", attach: '[data-testid="sellix-logo"]',       placement: "right",  title: "Contact — every relationship Maya touched", text: "Lead score, last touchpoint, signal source, open deals. Contact Master gets the full reframe in Phase 7b." },
  { id: "outreach",    route: "/outreach", attach: '[data-testid="goto-campaigns"]',  placement: "left",   title: "Outreach — sent as you, not as a tool", text: "Sequences across LinkedIn, Email, WhatsApp, Voice. Maya warms each channel and rotates deliverability." },
  { id: "sequence",    route: "/outreach/sequences/seq-cmp-1", attach: '[data-testid="node-palette"]', placement: "right", title: "Visual sequence builder", text: "Drag steps, draw branches, A/B test, and let Maya write the first line in your voice. This sequence mixes LinkedIn → Email → AI Voice." },
  { id: "ai-sequence", route: "/outreach/sequences/seq-cmp-1", attach: '[data-testid="ai-suggest-btn"]', placement: "bottom", title: "AI Sequence Suggestions", text: "Tell Sellyx the persona, goal, channels and length — Maya drafts a sequence and drops it on the canvas. Accept or discard." },
  { id: "signals",     route: "/signals", attach: '[data-testid="signals-rules-btn"]', placement: "left",  title: "Signals — leads finding you",        text: "Reddit, LinkedIn, Quora, HN, G2 — Maya watches the public web for buying intent and surfaces the threads worth replying to." },
  { id: "content",     route: "/content", attach: '[data-testid="content-calendar"]', placement: "right",  title: "Maya writes in your voice",           text: "LinkedIn, X, Reddit, Quora, Medium, blog, newsletter, Telegram — all in one calendar. Drag a post to reschedule." },
  { id: "studio",      route: "/content/studio", attach: '[data-testid="studio-generate-btn"]', placement: "top", title: "AI Content Studio",   text: "Brief in → channel-perfect content out. Use a signal or doc as source. One brief, ten channels — adapted, not copy-pasted." },
  { id: "brain",       route: "/brain", attach: '[data-testid="kb-source-card-custom"]', placement: "left",   title: "Brain — what Maya knows",            text: "SharePoint, Drive, OneDrive, URLs, voice memos — Sellyx crawls, chunks and embeds. Maya cites her sources." },
  { id: "playground",  route: "/brain/playground", attach: '[data-testid="rag-run"]', placement: "right",  title: "Test what Maya knows",               text: "Run any query through the RAG pipeline. See exactly which chunks come back, with which scores — before Maya answers a single customer." },
  { id: "team",        route: "/settings/team", attach: '[data-testid="invite-team"]', placement: "left",  title: "Team — up to 5 collaborators",       text: "One instance, one Maya, up to 5 humans. Owner / Editor / Reviewer roles. Every action attributed." },
  { id: "performance", route: "/settings/performance", attach: '[data-testid="sellix-logo"]', placement: "right", title: "Maya's impact this month",  text: "4,842 conversations deflected · 1,184h SDR time saved · $842K attributed · 5.2 FTE replaced. The slide for your next board deck." },
  { id: "connections", route: "/settings/connections", attach: '[data-testid="sellix-logo"]', placement: "right", title: "Pair channels with one scan", text: "Aman's WhatsApp · LinkedIn · Gmail · Calendar · Telegram. QR-based pairing via OpenClaw — no APIs, no tokens. Ships Phase 8." },
  { id: "instance",    route: "/settings/instance", attach: '[data-testid="instance-private-saas"]', placement: "right", title: "Private SaaS · Hardware · Self-hosted", text: "Where Maya lives. Pick the model that fits your data + control needs. Default Private SaaS at $999/mo." },
  { id: "done",        route: "/today", attach: '[data-testid="sellix-logo"]',        placement: "right",  title: "That's Sellyx.",                     text: "Ready to demo. ⌘K to jump anywhere; restart this tour anytime from your avatar menu." },
];

function resolveSteps() {
  const auth = readAuth();
  const persona = auth?.persona;
  const recipient = auth?.recipient || {};
  const overrides = personaCopy[persona] || {};
  const steps = (TEAM_INVITES_UI_ENABLED ? baseSteps : baseSteps.filter((s) => s.id !== "team"))
    .filter((s) => CONTENT_ENABLED || (s.id !== "content" && s.id !== "studio"));
  return steps.map((s) => {
    let copy = overrides[s.id] || { title: s.title, text: s.text };
    if (s.id === "welcome" && recipient.name && recipient.company) {
      copy = { ...copy, title: `${recipient.name} — welcome to Sellyx`, text: `Sellyx configured for ${recipient.company}. Your AI SDR Maya, ready in your accounts. 90 seconds.` };
    }
    return { ...s, title: copy.title, text: copy.text };
  });
}

let activeTour = null;

function buildTour(navigate, onBookDemo) {
  const auth = readAuth();
  const isDemo = auth?.mode === "demo";
  const steps = resolveSteps();
  const tour = new Shepherd.Tour({
    useModalOverlay: true,
    defaultStepOptions: {
      classes: "sellix-shepherd",
      scrollTo: { behavior: "smooth", block: "center" },
      cancelIcon: { enabled: true },
      modalOverlayOpeningPadding: 6,
      modalOverlayOpeningRadius: 10,
    },
  });

  steps.forEach((s, i) => {
    const last = i === steps.length - 1;
    let buttons;
    if (last && isDemo) {
      buttons = [
        { text: "Restart tour", classes: "shepherd-button-secondary", action: () => { tour.complete(); setTimeout(() => startProductTour(navigate, onBookDemo), 200); } },
        { text: "Book a live demo", action: () => {
          tour.complete();
          try { window.dispatchEvent(new CustomEvent("sellix:book-demo")); } catch { /* noop */ }
          if (onBookDemo) onBookDemo();
        } },
      ];
    } else {
      buttons = [
        i > 0 && { text: "Back", classes: "shepherd-button-secondary", action: () => tour.back() },
        { text: last ? "Finish" : "Next", action: () => (last ? tour.complete() : tour.next()) },
      ].filter(Boolean);
    }
    tour.addStep({
      id: s.id,
      title: s.title,
      text: last && isDemo ? `${s.text}\n\nReady to talk pricing or implementation? Book 30 minutes with a real human.` : s.text,
      attachTo: { element: s.attach, on: s.placement },
      beforeShowPromise: () => new Promise((resolve) => {
        if (window.location.pathname !== s.route) {
          navigate(s.route);
          setTimeout(resolve, 480);
        } else {
          resolve();
        }
      }),
      buttons,
    });
  });

  tour.on("complete", () => { try { localStorage.setItem(TOUR_KEY, "1"); } catch { /* noop */ } });
  tour.on("cancel", () => { try { localStorage.setItem(TOUR_KEY, "1"); } catch { /* noop */ } });
  return tour;
}

export function startProductTour(navigate, onBookDemo) {
  if (activeTour) activeTour.cancel();
  activeTour = buildTour(navigate, onBookDemo);
  activeTour.start();
}

export function useAutoTour(navigate, onBookDemo) {
  const fired = useRef(false);
  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    const auth = readAuth();
    const isDemo = auth?.mode === "demo";
    let seen = null;
    try { seen = localStorage.getItem(TOUR_KEY); } catch { /* noop */ }
    if (isDemo || !seen) {
      setTimeout(() => startProductTour(navigate, onBookDemo), isDemo ? 600 : 700);
    }
  }, [navigate, onBookDemo]);
}

export function resetTourFlag() {
  try { localStorage.removeItem(TOUR_KEY); } catch { /* noop */ }
}
